import { PlusIcon } from "lucide-react";
import { useState } from "react";
import { Form, useForm } from "react-hook-form";
import { ClassNamesConfig, GroupBase } from "react-select";
import AsyncSelect from "react-select/async";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  SearchIndicator,
  createClassNames,
} from "@/components/ui/react-select";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { UserOption } from "@/features/session/types/user-option";
import { getUsers } from "@/features/users/api/get-users";
import { UserSelectOption } from "@/features/users/components/user-select-option";
import { queryClient } from "@/lib/react-query";
import { cn } from "@/lib/utils";
import { PublicEvent } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { useAddParticipantToEvent } from "../api/add-participant-to-event";
import { getEventQueryKey } from "../api/get-event";
import {
  AddAttendeeFormData,
  addAttendeeSchema,
} from "../types/add-attendee.schema";

import { AddAttendeeModalTable } from "./add-attendee-modal-table";

type AddAttendeeModalProps = {
  event: PublicEvent;
};

export function AddAttendeeModal({ event }: AddAttendeeModalProps) {
  const { organisation } = useOrganisation();
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState("");
  const [attendees, setAttendees] = useState<UserOption[]>([]);

  const form = useForm<AddAttendeeFormData>({
    resolver: zodResolver(addAttendeeSchema),
  });

  const addAttendee = (attendee: UserOption) => {
    setAttendees([...attendees, attendee]);
  };

  const guestsError = form.formState.errors.guests;
  const reactSelectClassNames = createClassNames({
    control: () => {
      return cn(!!guestsError && "border-destructive");
    },
  });

  const loadOptions = async (input: string): Promise<UserOption[]> => {
    const users = await getUsers({
      organisationId: organisation.id,
      pageSize: 20,
      pageIndex: 0,
      search: input,
    });
    const filteredUsers = users.items.filter(
      (user) =>
        !attendees.some((attendee) => attendee.email === user.email) &&
        !event.session.guests.includes(user.email) &&
        event.session.organiser.id !== user.id,
    );

    return filteredUsers.map((user) => ({
      userId: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      label: `${user.firstName} ${user.lastName}`,
      email: user.email,
    }));
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setAttendees([]);
    }
    setIsOpen(open);
  };

  const { mutateAsync: addAttendeesToEvent, isPending } =
    useAddParticipantToEvent({
      onSuccess: (data) => {
        setIsOpen(false);
        setAttendees([]);
        queryClient.setQueryData(getEventQueryKey(event.id), data);
      },
    });

  const handleSubmit = () => {
    if (!organisation?.id) {
      return;
    }

    const participants = attendees.map((attendee) => ({
      userId: attendee.userId,
    }));
    addAttendeesToEvent({
      eventId: event.id,
      orgId: organisation.id,
      payload: { participants },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="mr-2 size-4" aria-hidden="true" /> Add User
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Users</DialogTitle>
        </DialogHeader>
        <DialogBody className="z-10 overflow-auto">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col gap-2">
                <Label htmlFor="attendee-name-or-email">
                  Attendee Name or Email
                </Label>
                <AsyncSelect<UserOption>
                  unstyled
                  components={{
                    DropdownIndicator: SearchIndicator,
                    ClearIndicator: () => null,
                    Option: UserSelectOption,
                    IndicatorSeparator: () => null,
                  }}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({
                      ...base,
                      pointerEvents: "auto",
                      zIndex: 100,
                    }),
                  }}
                  classNames={
                    reactSelectClassNames as ClassNamesConfig<
                      UserOption,
                      false,
                      GroupBase<UserOption>
                    >
                  }
                  ref={form.register("guests").ref}
                  placeholder="Type attendee name or email"
                  noOptionsMessage={() => "Type attendee name or email"}
                  loadOptions={loadOptions}
                  isDisabled={form.formState.isSubmitting}
                  inputValue={value}
                  onInputChange={(value: string) => {
                    setValue(value);
                  }}
                  inputId="attendee-name-or-email"
                  value={null}
                  onChange={(value) => {
                    if (value) {
                      addAttendee(value);
                      setValue("");
                      return null;
                    }
                  }}
                />
              </div>
              <AddAttendeeModalTable
                participants={attendees}
                setParticipants={setAttendees}
              />
            </form>
          </Form>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            variant="default"
            type="submit"
            disabled={attendees.length === 0 || isPending}
            isAnimated={isPending}
            isLoading={isPending}
            onClick={handleSubmit}
          >
            Add Users
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
